import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Hourglass, Inbox } from 'lucide-react';
import { EinstLink, pfadVon } from '../pages/einstellungen/EinstLink';
import { naechstesZiel } from '../pages/einstellungen/fokus';
import { istEinstellungsPfad } from '../pages/einstellungen/seiten';
import { alsGelesen, artVon, BENACHRICHTIGUNGEN_PFAD, BENACHRICHTIGUNGEN_TEXTE, datumZeit, EMAIL_ANKER, ladePosteingang, ladeZaehler,
  PosteingangEintrag, zielVon } from '../pages/einstellungen/benachrichtigungen';
import { zoomVon } from './Erklaerung';
import './glocke.css';

// Profil-Nachbau F-10 (Bennys Freigabe 14.10.2026, BAU-BENACHRICHTIGUNG.md 1.2 und 5.1, PF-607): die Glocke im Kopf der
// App-Seitenleiste mit dem Zähler der ungelesenen Einträge und einem Fenster mit den Reitern „Ungelesen“ und „Alle“. Ein Klick
// auf einen Eintrag markiert ihn gelesen, schließt das Fenster und öffnet sein Ziel (zielVon: ein Ereignis sein Ziel, ein
// Rundschreiben seine Zeile auf der Seite „Benachrichtigungen“). Typen und Aufrufe kommen aus benachrichtigungen.ts (PF-606).
// Die Seitenleiste setzt die Glocke nur außerhalb einer Sicht ein (AppSidebar.tsx); inhaber ergänzt für die Leiste der
// Plattform-Verwaltung selbst eine Eigenschaft für die Lage (PF-413-4).
export const GLOCKE_TEXTE = {
  titel: BENACHRICHTIGUNGEN_TEXTE.titel,
  mitUngelesenen: (anzahl: number) => `${BENACHRICHTIGUNGEN_TEXTE.titel}, ${anzahl} ungelesen`,
  ungelesen: BENACHRICHTIGUNGEN_TEXTE.ungelesen,
  alle: 'Alle',
  leerUngelesen: 'Alles gelesen',
  leerUngelesenSatz: 'Gerade gibt es nichts Neues.',
  leerAlle: 'Noch keine Benachrichtigungen',
  leerAlleSatz: 'Sobald es Neues zu Ihrem Konto gibt, finden Sie es hier.',
  alleBenachrichtigungen: 'Alle Benachrichtigungen',
  emailsEinstellen: 'E-Mails einstellen',
  laden: 'Wird geladen …',
  ladeFehler: BENACHRICHTIGUNGEN_TEXTE.ladeFehler,
  erneut: BENACHRICHTIGUNGEN_TEXTE.erneut,
} as const;
const T = GLOCKE_TEXTE;

export type Reiter = 'ungelesen' | 'alle';
const REITER: Reiter[] = ['ungelesen', 'alle'];
/** Die Liste im Fenster: ohne eintraege und ohne fehler lädt sie noch. */
export type ListenStand = { eintraege?: PosteingangEintrag[]; fehler?: boolean };

/** Der Punkt oben rechts: 1 bis 99, darüber „99+“; ohne Ungelesene keiner. */
export const zaehlerText = (ungelesen: number) => (ungelesen <= 0 ? null : ungelesen > 99 ? '99+' : String(ungelesen));
/** aria-label und title der Glocke. */
export const glockeName = (ungelesen: number) => (ungelesen > 0 ? T.mitUngelesenen(ungelesen) : T.titel);
/** Die Zeile unter dem Betreff: „{Art} · {TT.MM.JJJJ, HH:MM}“. */
export const eintragMeta = (eintrag: Pick<PosteingangEintrag, 'createdAt' | 'category' | 'ereignis'>) =>
  `${artVon(eintrag)} · ${datumZeit(eintrag.createdAt)}`;

// Ziele in den Einstellungen öffnen wie aus dem Kontomenü (EinstLink), alle übrigen als gewöhnlicher Link (UserMenu.tsx).
function ZielLink({ to, className, onClick, children }: { to: string; className: string; onClick: () => void; children: React.ReactNode }) {
  return istEinstellungsPfad(pfadVon(to))
    ? <EinstLink to={to} className={className} onClick={onClick}>{children}</EinstLink>
    : <Link to={to} className={className} onClick={onClick}>{children}</Link>;
}

/** Inhalt eines Reiters: Laden, Fehler mit „Erneut versuchen“, leer je Reiter oder die Einträge (ungelesen halbfett). */
export function GlockeListe({ reiter, stand, onWaehlen, onErneut }: { reiter: Reiter; stand: ListenStand; onWaehlen: (eintrag: PosteingangEintrag) => void; onErneut: () => void }) {
  if (stand.fehler) return <div className="glocke-hinweis"><p role="alert">{T.ladeFehler}</p>
    <button type="button" className="glocke-erneut" onClick={onErneut}>{T.erneut}</button></div>;
  if (!stand.eintraege) return <p className="glocke-hinweis glocke-laden" role="status" aria-busy="true" data-laden><Hourglass size={16} aria-hidden="true" />{T.laden}</p>;
  if (!stand.eintraege.length) {
    const [Bild, titel, satz] = reiter === 'ungelesen' ? [CheckCheck, T.leerUngelesen, T.leerUngelesenSatz] as const : [Inbox, T.leerAlle, T.leerAlleSatz] as const;
    return <div className="glocke-leer"><Bild size={24} aria-hidden="true" /><strong>{titel}</strong><p>{satz}</p></div>;
  }
  return <ul className="glocke-liste">{stand.eintraege.map(eintrag => <li key={eintrag.id}>
    <ZielLink to={zielVon(eintrag)} className={eintrag.readAt ? 'glocke-eintrag' : 'glocke-eintrag glocke-ungelesen'} onClick={() => onWaehlen(eintrag)}>
      {!eintrag.readAt && <span className="sr-only">{T.ungelesen}: </span>}
      <span className="glocke-betreff">{eintrag.subject}</span><span className="glocke-meta">{eintragMeta(eintrag)}</span>
    </ZielLink>
  </li>)}</ul>;
}

/** Das Fenster: Kopf, Reiter (Pfeiltasten wechseln), Inhalt und Fuß mit den Wegen zur Seite „Benachrichtigungen“. */
export function GlockeFenster({ id, fensterRef, reiter, stand, onReiter, onWaehlen, onErneut, onSchliessen }: {
  id: string; fensterRef?: React.Ref<HTMLDivElement>; reiter: Reiter; stand: ListenStand; onReiter: (reiter: Reiter) => void;
  onWaehlen: (eintrag: PosteingangEintrag) => void; onErneut: () => void; onSchliessen: () => void;
}) {
  const reiterTasten = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const ziel = naechstesZiel(REITER.length, REITER.indexOf(reiter), event.key === 'ArrowLeft');
    onReiter(REITER[ziel]);
    event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[ziel]?.focus();
  };
  return <div ref={fensterRef} id={id} role="dialog" aria-label={T.titel} className="glocke-fenster">
    <h2 className="glocke-titel">{T.titel}</h2>
    <div role="tablist" aria-label={T.titel} className="glocke-reiter" onKeyDown={reiterTasten}>
      {REITER.map(r => <button key={r} type="button" role="tab" id={`${id}-${r}`} aria-selected={reiter === r} aria-controls={`${id}-liste`}
        tabIndex={reiter === r ? 0 : -1} onClick={() => onReiter(r)}>{T[r]}</button>)}
    </div>
    <div role="tabpanel" id={`${id}-liste`} aria-labelledby={`${id}-${reiter}`} className="glocke-inhalt">
      <GlockeListe reiter={reiter} stand={stand} onWaehlen={onWaehlen} onErneut={onErneut} />
    </div>
    <div className="glocke-fuss">
      <EinstLink to={BENACHRICHTIGUNGEN_PFAD} onClick={onSchliessen}>{T.alleBenachrichtigungen}</EinstLink>
      <EinstLink to={`${BENACHRICHTIGUNGEN_PFAD}#${EMAIL_ANKER}`} onClick={onSchliessen}>{T.emailsEinstellen}</EinstLink>
    </div>
  </div>;
}

// Wie SeitenRahmen.tsx: unter 800 px ist die Leiste einspaltig über dem Inhalt.
const SCHMAL = '(max-width: 799px)';

// PF-413-4 (F-10, F-21 Muss 1): lage „leiste“ setzt die Glocke als volle Zeile in die Leiste der Plattform-Verwaltung
// (seit Profil-Nachbau F-22 als letzter Eintrag der Gruppe „Überblick“, Administration.tsx und verwaltungsLeiste.ts
// mitEintragImUeberblick), mit Symbol, Wort und Zähler wie ein Leisteneintrag; das Fenster öffnet
// ab 800 px rechts neben der Zeile, schmal wie in der App darunter. Vorgabe „kopf“ ist die App-Seitenleiste wie bisher.
export default function Glocke({ lage = 'kopf' }: { lage?: 'kopf' | 'leiste' } = {}) {
  const [ungelesen, setUngelesen] = useState(0);
  const [offen, setOffen] = useState(false);
  const [reiter, setReiter] = useState<Reiter>('ungelesen');
  const [stand, setStand] = useState<ListenStand>({});
  const [ladung, setLadung] = useState(0);
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const fenster = useRef<HTMLDivElement>(null);
  const zaehlerAbbruch = useRef<AbortController | null>(null);
  const id = useId();

  // Fehler (auch 401 und 403) blenden nur den Zähler aus, ohne Meldung.
  const holen = useCallback(() => {
    zaehlerAbbruch.current?.abort();
    const abbruch = new AbortController();
    zaehlerAbbruch.current = abbruch;
    ladeZaehler(abbruch.signal).then(antwort => { if (!abbruch.signal.aborted) setUngelesen(antwort.ungelesen); },
      () => { if (!abbruch.signal.aborted) setUngelesen(0); });
  }, []);

  // Zähler beim Start, alle 60 s solange das Fenster sichtbar ist und beim Zurückkehren in den Tab; ein Zeitgeber je Glocke.
  useEffect(() => {
    holen();
    const sichtbar = () => { if (document.visibilityState === 'visible') holen(); };
    const takt = window.setInterval(sichtbar, 60_000);
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      window.clearInterval(takt);
      document.removeEventListener('visibilitychange', sichtbar);
      zaehlerAbbruch.current?.abort();
    };
  }, [holen]);

  // Die Liste des Reiters, bei jedem Öffnen und Wechsel neu (erste Seite, 20 Einträge; alle übrigen auf der Seite).
  useEffect(() => {
    if (!offen) return;
    const abbruch = new AbortController();
    setStand({});
    ladePosteingang(1, reiter === 'ungelesen', abbruch.signal).then(antwort => { if (!abbruch.signal.aborted) setStand({ eintraege: antwort.items }); },
      () => { if (!abbruch.signal.aborted) setStand({ fehler: true }); });
    return () => abbruch.abort();
  }, [offen, reiter, ladung]);

  // Das Fenster steht unter der Glocke und bleibt im Bildschirm (8 px Rand). Die Leiste bindet fixierte Elemente an sich,
  // deshalb erst bei 0/0 messen (wie das Menü „…“ in AppSidebar.tsx). Beim Öffnen geht der Fokus auf den gewählten Reiter.
  // In der Leiste der Verwaltung ab 800 px rechts neben der Zeile, oben bündig mit ihr; mindestens 200 px bleiben im Bild.
  useLayoutEffect(() => {
    const k = knopf.current, f = fenster.current;
    if (!offen || !k || !f) return;
    f.style.left = '0px'; f.style.top = '0px';
    const basis = f.getBoundingClientRect(), r = k.getBoundingClientRect(), z = zoomVon(f);
    const daneben = lage === 'leiste' && !window.matchMedia(SCHMAL).matches;
    const top = daneben ? Math.max(8, Math.min(r.top, window.innerHeight - 8 - 200 * z)) : r.bottom + 4;
    const left = Math.min(Math.max(8, daneben ? r.right + 8 : r.left), Math.max(8, window.innerWidth - f.offsetWidth * z - 8));
    f.style.left = `${Math.round((left - basis.left) / z)}px`; f.style.top = `${Math.round((top - basis.top) / z)}px`;
    f.style.maxHeight = `${Math.max(200, Math.floor((window.innerHeight - top - 8) / z))}px`;
    f.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus({ preventScroll: true });
  }, [offen, lage]);

  // Klick daneben schließt; der Fokus kehrt auf die Glocke zurück, außer der Klick gab ihn einem anderen Bedienelement.
  useEffect(() => {
    if (!offen) return;
    const aussen = (event: MouseEvent) => {
      if (huelle.current?.contains(event.target as Node)) return;
      setOffen(false);
      requestAnimationFrame(() => {
        const aktiv = document.activeElement;
        if (!aktiv || aktiv === document.body || !aktiv.isConnected) knopf.current?.focus({ preventScroll: true });
      });
    };
    const zu = () => setOffen(false);
    // Das fixierte Fenster folgt der Glocke nicht beim Bildlauf ihrer Vorfahren; die eigene Liste bleibt offen.
    const bildlauf = (event: Event) => {
      const ziel = event.target;
      if (ziel instanceof Node && knopf.current && ziel.contains(knopf.current)) zu();
    };
    document.addEventListener('mousedown', aussen, true);
    document.addEventListener('scroll', bildlauf, true);
    window.addEventListener('resize', zu);
    return () => {
      document.removeEventListener('mousedown', aussen, true);
      document.removeEventListener('scroll', bildlauf, true);
      window.removeEventListener('resize', zu);
    };
  }, [offen]);

  // Escape schließt nur das Fenster (die Hülle klappt sonst am Handy die Leiste ein) und gibt den Fokus der Glocke.
  const tasten = (event: React.KeyboardEvent) => {
    if (!offen || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    setOffen(false);
    knopf.current?.focus({ preventScroll: true });
  };
  const umschalten = () => {
    if (offen) { setOffen(false); return; }
    setReiter('ungelesen');
    setOffen(true);
    holen();
  };
  const waehlen = (eintrag: PosteingangEintrag) => {
    setOffen(false);
    if (!eintrag.readAt) void alsGelesen(eintrag.id).then(holen, holen);
  };

  const name = glockeName(ungelesen), zahl = zaehlerText(ungelesen);
  const zeile = lage === 'leiste';
  return (
    <div
      ref={huelle}
      className={zeile ? 'glocke glocke--leiste' : 'glocke'}
      onKeyDown={tasten}
      // Verlässt der Fokus Glocke und Fenster (Tab), schließt es; ohne neues Ziel fängt den Klick daneben der Zuhörer oben.
      onBlur={event => { if (offen && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOffen(false); }}
    >
      <button
        ref={knopf}
        type="button"
        aria-label={name}
        title={name}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-controls={offen ? id : undefined}
        onClick={umschalten}
        className={zeile ? 'glocke-knopf glocke-zeile' : 'glocke-knopf relative h-11 w-11 flex items-center justify-center hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors'}
      >
        {zeile
          ? <><Bell size={16} className="glocke-symbol" aria-hidden="true" /><span className="glocke-wort">{T.titel}</span></>
          : <Bell className="w-4 h-4 text-muted" aria-hidden="true" />}
        {zahl && <span className={zeile ? 'glocke-zaehler glocke-zaehler--zeile' : 'glocke-zaehler'} aria-hidden="true">{zahl}</span>}
      </button>
      {offen && (
        <GlockeFenster id={id} fensterRef={fenster} reiter={reiter} stand={stand} onReiter={setReiter} onWaehlen={waehlen}
          onErneut={() => setLadung(n => n + 1)} onSchliessen={() => setOffen(false)} />
      )}
    </div>
  );
}
